import React, { useState, useEffect } from 'react';
import { FormControl, Container, Box, FormLabel, RadioGroup } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FormDialog } from '../../../../components/form-dialog';
import { Row } from '../../../../components/row';
import { Cell } from '../../../../components/cell';
import { ColorPalette } from '../../../../components/config';
import { MicInputSelector } from '../../../../components/mic-input-selector';
import useAudioInput from '../../../../hooks/audio/use-audio-input';
import { RadioOption } from '../../../../components/radio-group';
import { MeetingForm } from "./dialog-support-interfaces";

interface DialogSupportStartModalProps {
  open: boolean;
  closeDialog: () => void;
  onStartRecording: (selectedMeetingForm: MeetingForm, selectedDeviceId?: string) => void;
}


export function DialogSupportStartModal(props: DialogSupportStartModalProps) {
  const { open, closeDialog, onStartRecording } = props;
  const [meetingForm, setMeetingForm] = useState<MeetingForm>('Personal');
  const { audioDevices, selectedDevice, setSelectedDevice } = useAudioInput();

  useEffect(() => {
    if (audioDevices.length === 0) return;

    const savedDeviceId = localStorage.getItem(getDeviceStorageKey(meetingForm));
    if (savedDeviceId) {
      const savedDevice = audioDevices.find(device => device.deviceId === savedDeviceId);
      if (savedDevice && savedDevice.deviceId !== selectedDevice?.deviceId) {
        setSelectedDevice(savedDevice);
      }
    }
  }, [audioDevices, meetingForm, selectedDevice, setSelectedDevice]);

  function handleMeetingFormChange(e: React.ChangeEvent<HTMLInputElement>) {
    setMeetingForm(e.target.value as MeetingForm);
  }

  function handleDeviceChange(device: MediaDeviceInfo) {
    setSelectedDevice(device);
    localStorage.setItem(getDeviceStorageKey(meetingForm), device.deviceId);
  }

  function getDeviceStorageKey(form: string) {
    return `journal-diary-dialog-support-lastSelectedDevice-${form}`;
  }

  return (
    <FormDialog
      open={open}
      title="Start Dialogstøtte?"
      confirmButtonText="Ja, start Dialogstøtte"
      cancelButtonText="Nej"
      onConfirmClick={() => {
        onStartRecording(meetingForm, selectedDevice?.deviceId);
        closeDialog();
      }}
      onCancelClick={() => {
        closeDialog();
      }}
      maxWidth="md"
      sx={{
        '& .MuiDialogTitle-root': {
          px: '44px',
        },
      }}
      dontCapitalizeButtonTexts
    >
      <FormControl fullWidth>
        <Container>
          <Box>
            <Row>
              <Cell size="full">Ønsker du at starte Dialogstøtte i forbindelse med dette notat?</Cell>
              <Cell size="full">
                Dialogstøtte dokumenterer og opsummerer samtalen med borgeren. Opsummeringen kan efterfølgende indsættes i
                notatfeltet og skal verificeres inden færdiggørelse.
              </Cell>
              <Cell size="full">Tryk &apos;Ja&apos; for at starte Dialogstøtte i forbindelse med notatet.</Cell>
            </Row>
            <Row>
              <Cell size="full">
                <FormControl>
                  <FormLabel>Vælg mødeform</FormLabel>
                  <RadioGroup
                    aria-labelledby="Vælg mødeform"
                    row
                    name="meetingForm"
                    value={meetingForm}
                    onChange={handleMeetingFormChange}
                  >
                    <RadioOption value="Personal" label="Personligt fremmøde" />
                    <RadioOption value="Digital" label="Via telefon eller digitalt (fx. via Teams/Skype)" />
                  </RadioGroup>
                </FormControl>
              </Cell>
            </Row>
            <MicInputSelector
              audioDevices={audioDevices}
              onDeviceChange={handleDeviceChange}
              selectedDeviceId={selectedDevice?.deviceId}
            />
            {(meetingForm === 'Personal' || meetingForm === 'Digital') && (
              <Box display="flex" alignItems="center" gap={3} my={1}>
                <FontAwesomeIcon size="2x" icon={faInfoCircle} color={ColorPalette.InfoIcon} />
                <Box
                  sx={{
                    padding: '10px',
                    border: 'solid 1px peru',
                    borderRadius: 1,
                    flexGrow: 2,
                    backgroundColor: ColorPalette.PickledEggyolk,
                  }}
                >
                  {meetingForm === 'Personal' ? (
                    <>
                      Ved samtaler med personligt fremmøde kræver det, at du tillader brug af computerens mikrofon for at
                      Dialogstøtte kan tilgå lyden fra samtalen. Vælg i så fald &apos;Tillad&apos; i browserdialogen.
                      <br />
                      <br />
                      Bemærk, at dialogen ikke vises, hvis du allerede tidligere har tilladt brug af mikrofon. I så fald
                      kan du fortsætte.
                    </>
                  ) : (
                    <>
                      Digitale møder kræver godkendelse af visse browserindstillinger for at Dialogstøtte kan tilgå lyden
                      fra samtalen.
                      <br />
                      <br />
                      I browserdialogen gør følgende:
                      <br />
                      1. Vælg fanen &apos;Hele skærmen&apos;
                      <br />
                      2. Markér &apos;Hele skærmen&apos; på fanen
                      <br />
                      3. Slå systemlyd til
                      <br />
                      4. Klik på knappen &apos;Del&apos;
                    </>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Container>
      </FormControl>
    </FormDialog>
  );
}
